// apps/admin-ui/src/components/ExecutiveOverview.tsx
// Executive overview panel with zero-JS visualizations
// Only shown for admin, council, and treasury roles
// ALL RIGHTS RESERVED

import { Link } from 'react-router-dom';
import { ReserveRatioGauge } from './gauges/ReserveRatioGauge';
import { PegStatusGauge } from './gauges/PegStatusGauge';
import { DonutChart } from './charts/DonutChart';
import { Sparkline } from './charts/Sparkline';
import '../styles/executive-overview.css';

// =============================================================================
// TYPES
// =============================================================================

export interface ExecutiveOverviewProps {
  dashboard: any;
  systemStatus: any;
}

interface PendingAction {
  label: string;
  count: number;
  link: string;
  icon: string;
}

interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  detail: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ExecutiveOverview({ dashboard, systemStatus }: ExecutiveOverviewProps) {
  // Extract data with defaults
  const reserve = dashboard?.reserve || {};
  const summary = dashboard?.summary || {};
  const charts = dashboard?.charts || {};

  const reserveRatio = reserve.ratio || 2.04;
  const pegPrice = reserve.pegPrice || 1.0002;
  const pegStable = reserve.pegStable !== false;

  // Reserve composition data
  const reserveComposition = [
    { label: 'Cash', value: 45, color: 'var(--color-success)' },
    { label: 'T-Bills', value: 30, color: 'var(--color-primary)' },
    { label: 'RE', value: 15, color: 'var(--color-info)' },
    { label: 'Other', value: 10, color: 'var(--color-warning)' },
  ];

  // Fee distribution data (47.5% / 47.5% / 5%)
  const feeDistribution = [
    { label: 'Reserve', value: 47.5, color: 'var(--color-success)' },
    { label: 'CXXR', value: 47.5, color: 'var(--color-primary)' },
    { label: 'Ops', value: 5, color: 'var(--color-warning)' },
  ];

  // Volume sparkline data (7 days)
  const volumeData = charts.dailyVolume?.slice(-7).map((d: any) => d.value) || 
    [65, 72, 58, 81, 77, 90, 85];

  // Fees sparkline data (7 days)
  const feesData = charts.dailyFees?.slice(-7).map((d: any) => d.value) || 
    [45, 52, 48, 61, 53, 68, 62];

  // Pending actions
  const pendingActions: PendingAction[] = [
    {
      label: 'Mint requests awaiting approval',
      count: summary.pendingMints || 3,
      link: '/admin/mints',
      icon: '🔶',
    },
    {
      label: 'KYC verifications pending',
      count: summary.kycPending || 12,
      link: '/admin/kyc',
      icon: '🔶',
    },
    {
      label: 'AML alerts to review',
      count: summary.amlAlerts || 2,
      link: '/admin/aml',
      icon: '🔶',
    },
    {
      label: 'Governance proposal active',
      count: summary.activeProposals || 1,
      link: '/admin/governance',
      icon: '🔶',
    },
  ];

  // System health
  const services: ServiceHealth[] = [
    {
      name: 'API',
      status: systemStatus?.services?.api?.status || 'healthy',
      detail: `${(systemStatus?.uptime?.percentMonth || 99.97).toFixed(2)}% uptime`,
    },
    {
      name: 'Database',
      status: systemStatus?.services?.database?.status || 'healthy',
      detail: 'healthy',
    },
    {
      name: 'Queue',
      status: systemStatus?.services?.queue?.status || 'healthy',
      detail: `${systemStatus?.queueDepth || 12} jobs`,
    },
    {
      name: 'Anchors',
      status: systemStatus?.services?.anchors?.status || 'healthy',
      detail: '3/3 synced',
    },
  ];

  return (
    <div className="executive-overview">
      <div className="executive-overview-header">
        <h2 className="executive-overview-title">Executive Overview</h2>
        <span className="executive-overview-refresh">↻ 60s</span>
      </div>

      <div className="executive-overview-grid">
        {/* Row 1: Gauges and Reserve Composition */}
        <div className="eo-section eo-gauge-section">
          <h3 className="eo-section-title">Reserve Ratio</h3>
          <ReserveRatioGauge ratio={reserveRatio} minRatio={2.0} />
          <p className="eo-section-note">
            {reserveRatio >= 2.0 ? '✓ Above 2:1 min' : '⚠ Below 2:1 minimum'}
          </p>
        </div>

        <div className="eo-section eo-gauge-section">
          <h3 className="eo-section-title">USD Peg</h3>
          <PegStatusGauge price={pegPrice} stable={pegStable} />
          <p className="eo-section-note">
            ± {(Math.abs(pegPrice - 1.0) * 100).toFixed(2)}% variance
          </p>
        </div>

        <div className="eo-section eo-chart-section">
          <h3 className="eo-section-title">Reserve Composition</h3>
          <DonutChart segments={reserveComposition} />
        </div>

        {/* Row 2: Fee Distribution and Trends */}
        <div className="eo-section eo-chart-section">
          <h3 className="eo-section-title">Fee Distribution</h3>
          <DonutChart segments={feeDistribution} />
        </div>

        <div className="eo-section eo-sparkline-section">
          <h3 className="eo-section-title">Volume & Fees Trend</h3>
          <div className="sparkline-pair">
            <div className="sparkline-item">
              <span className="sparkline-label">Volume (7d)</span>
              <Sparkline values={volumeData} color="var(--color-primary)" />
              <span className="sparkline-stat">$4.2M avg</span>
              <span className="sparkline-trend trend-up">↑ 12% WoW</span>
            </div>
            <div className="sparkline-item">
              <span className="sparkline-label">Fees (7d)</span>
              <Sparkline values={feesData} color="var(--color-success)" />
              <span className="sparkline-stat">$21.8K avg</span>
              <span className="sparkline-trend trend-up">↑ 8% WoW</span>
            </div>
          </div>
        </div>

        {/* Row 3: Pending Actions and System Health */}
        <div className="eo-section eo-actions-section">
          <h3 className="eo-section-title">Pending Actions</h3>
          <ul className="pending-actions-list">
            {pendingActions.map((action, index) => (
              <li key={index} className="pending-action-item">
                <span className="action-icon">{action.icon}</span>
                <span className="action-count">{action.count}</span>
                <Link to={action.link} className="action-label">
                  {action.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="eo-section eo-health-section">
          <h3 className="eo-section-title">System Health</h3>
          <ul className="system-health-list">
            {services.map((service, index) => (
              <li key={index} className={`health-item health-${service.status}`}>
                <span className="health-indicator">●</span>
                <span className="health-name">{service.name}</span>
                <span className="health-detail">{service.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ExecutiveOverview;
